import React from "react";
import { ThemeToggle } from "./ui/ThemeToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "./ui/dropdown-menu";
import { LogOut, User, Bell, RefreshCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PATHS } from "./config/paths";
import { cn } from "./ui/utils";

interface NavbarProps {
  theme: "light" | "dark";
  setTheme: (theme: "light" | "dark") => void;
  onMenuClick?: () => void;
  currentUser?: { email: string; role: string } | null;
  onLogout?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  theme,
  setTheme,
  onMenuClick,
  currentUser,
  onLogout,
}) => {
  const navigate = useNavigate();
  const [lastSync, setLastSync] = React.useState(new Date());
  const [isSyncing, setIsSyncing] = React.useState(false);
  const [timeAgo, setTimeAgo] = React.useState("Just now");

  // Logic to calculate time ago
  React.useEffect(() => {
    const updateTime = () => {
      const diffInSeconds = Math.floor((new Date().getTime() - lastSync.getTime()) / 1000);
      if (diffInSeconds < 60) setTimeAgo("Just now");
      else if (diffInSeconds < 3600) setTimeAgo(`${Math.floor(diffInSeconds / 60)}m ago`);
      else setTimeAgo(`${Math.floor(diffInSeconds / 3600)}h ago`);
    };

    const timer = setInterval(updateTime, 10000); // Update every 10s
    return () => clearInterval(timer);
  }, [lastSync]);

  // Simulated background sync every 2 minutes
  React.useEffect(() => {
    const syncInterval = setInterval(() => {
      setIsSyncing(true);
      setTimeout(() => {
        setLastSync(new Date());
        setIsSyncing(false);
      }, 2000);
    }, 120000); // 2 minutes

    return () => clearInterval(syncInterval);
  }, []);

  const getInitials = (email: string) => {
    if (!email) return "??";
    return email.substring(0, 2).toUpperCase();
  };

  const initials = currentUser ? getInitials(currentUser.email) : "??";

  return (
    <nav
      className="
        flex items-center justify-between px-6 h-16 
        bg-navbar 
        backdrop-blur-2xl 
        border-b border-slate-200/60 dark:border-white/[0.05]
        shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07),0_4px_6px_-2px_rgba(0,0,0,0.02)] dark:shadow-[0_4px_30px_rgba(0,0,0,0.3)]
        z-40 sticky top-0 print:hidden
      "
    >
      <div className="flex items-center gap-4">
        <button
          onClick={onMenuClick}
          className="p-2 rounded-xl hover:bg-sidebar-accent active:scale-95 transition-all md:hidden"
        >
          <span className="text-xl">☰</span>
        </button>

        <img
          src="/logo12.png"
          alt="XeroAlpha"
          className="h-9 w-auto drop-shadow-md hover:scale-105 transition-transform"
        />
      </div>

      <div className="flex items-center gap-3">
        <div className="hidden md:flex items-center gap-3 px-4 py-1.5 rounded-full 
          bg-sidebar-accent/50 
          backdrop-blur-xl 
          border border-slate-200 dark:border-white/[0.15]
          shadow-inner transition-all duration-500">
          <div className="relative flex items-center justify-center">
            <div className={cn(
              "w-1.5 h-1.5 rounded-full",
              isSyncing ? "bg-blue-400" : "bg-emerald-400 animate-pulse"
            )} />
            {isSyncing && (
              <RefreshCcw className="absolute h-3 w-3 text-blue-400 animate-spin" />
            )}
          </div>
          <span className="text-[10px] uppercase font-semibold tracking-widest text-muted-foreground/80 min-w-[70px]">
            {isSyncing ? "Syncing..." : `Last sync: ${timeAgo}`}
          </span>
        </div>

        <ThemeToggle theme={theme} setTheme={setTheme} />

        {currentUser && (
          <div className="flex items-center gap-2 ml-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="
                  relative flex items-center justify-center w-10 h-10 rounded-xl
                  bg-white/[0.03]
                  backdrop-blur-xl
                  border border-slate-200 dark:border-white/[0.15]
                  text-primary font-semibold
                  hover:bg-white/[0.08] hover:scale-105 active:scale-95 
                  transition-all duration-300
                  shadow-lg
                ">
                  <span className="absolute inset-0 rounded-xl bg-primary/20 blur-xl opacity-0 hover:opacity-100 transition duration-500" />
                  <span className="relative z-10">{initials}</span>
                </button>
              </DropdownMenuTrigger>

              <DropdownMenuContent
                align="end"
                className="
                  w-56 p-2 rounded-2xl
                  bg-popover
                  backdrop-blur-2xl
                  border border-border-subtle
                  shadow-2xl
                "
              >
                <DropdownMenuLabel className="px-3 py-2">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
                      Logged in as {initials}
                    </span>
                    <span className="text-[10px] text-muted-foreground truncate">
                      {currentUser.email}
                    </span>
                  </div>
                </DropdownMenuLabel>

                <DropdownMenuSeparator className="bg-border-subtle my-1" />

                <DropdownMenuItem
                  onClick={() => navigate(PATHS.VIEW_PROFILE)}
                  className="flex items-center gap-2 p-3 rounded-xl cursor-pointer hover:bg-sidebar-accent transition-colors"
                >
                  <User size={16} />
                  <span className="font-medium text-sm">View Profile</span>
                </DropdownMenuItem>

                <DropdownMenuItem
                  onClick={() => navigate(PATHS.NOTIFICATIONS)}
                  className="flex items-center gap-2 p-3 rounded-xl cursor-pointer hover:bg-sidebar-accent transition-colors"
                >
                  <Bell size={16} />
                  <span className="font-medium text-sm">Notifications</span>
                </DropdownMenuItem>

                <DropdownMenuSeparator className="bg-border-subtle my-1" />

                <DropdownMenuItem
                  onClick={onLogout}
                  className="flex items-center gap-2 p-3 rounded-xl 
                  text-destructive hover:bg-destructive/10 
                  transition-colors cursor-pointer"
                >
                  <LogOut size={16} />
                  <span className="font-semibold text-sm">Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      <div className="absolute inset-x-0 bottom-0 h-[1px] bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
    </nav>
  );
};